// frontend/src/pages/AuditAssessment.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import { calculateRobustRisk } from "../lib/RiskEngine";
import { getRelevantQuestions, normalizeJurisdictions, getQuestionBankVersion } from "../lib/riskQuestionSet";
import computeSeverityFromAnswers from "../lib/severityModel";
import { ShieldAlert, Info } from "lucide-react";

import { appendAuditSnapshot } from "../lib/auditHistory";
import useEntitlement from "../hooks/useEntitlement";
import { requiresPaidQuota, getOrCreatePendingAssessmentId, clearPendingAssessmentId, finalizePaidAssessment } from "../lib/paidAssessmentQuota";
import { computePaidReportExposure } from "../lib/paidExposureEngine";
import { getFxRatesPlanning } from "../lib/fxRates";

const PULSE_MS = 650;
const FX_TIMEOUT_MS = 9000;
const FINALIZE_WATCHDOG_MS = 25000;

const STORAGE_KEYS = {
  ACTIVE_BUSINESS_ID: "aifine_active_business_id_v1",
  BUSINESS_PAYLOAD_PREFIX: "aifine_business_payload_",
  CONFIG: "aifine_config",
  ANSWERS_PREFIX: "aifine_answers_business_",
  PROGRESS_PREFIX: "aifine_assessment_progress_business_",
  RESULTS_LEGACY: "aifine_results",
  RESULTS_CANONICAL: "aifine_public_audit_results",
  RESULTS_CANONICAL_BUSINESS_PREFIX: "aifine_public_audit_results_business_",
};

// finalize lock keys (prevents double-run on same snapshot id)
const FINALIZE_LOCK_PREFIX = "aifine_finalize_lock_snapshot_";

function safeParse(raw, fallback = null) {
  try {
    if (!raw) return fallback;
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

function clampInt(x, lo, hi) {
  const n = Number(x);
  if (!Number.isFinite(n)) return lo;
  return Math.max(lo, Math.min(hi, Math.floor(n)));
}

function isAnsweredValue(v) {
  return v === true || v === false || v === "unsure" || v === "YES" || v === "NO" || v === "UNSURE";
}

function countAnswered(activeQuestions, answersObj) {
  const q = Array.isArray(activeQuestions) ? activeQuestions : [];
  const a = answersObj && typeof answersObj === "object" ? answersObj : {};
  return q.reduce((acc, item) => {
    const id = item?.id;
    if (!id) return acc;
    return acc + (isAnsweredValue(a[id]) ? 1 : 0);
  }, 0);
}

function findFirstUnansweredIndex(activeQuestions, answersObj) {
  const q = Array.isArray(activeQuestions) ? activeQuestions : [];
  const a = answersObj && typeof answersObj === "object" ? answersObj : {};
  for (let i = 0; i < q.length; i += 1) {
    const id = q[i]?.id;
    if (!id) continue;
    if (!isAnsweredValue(a[id])) return i;
  }
  return 0;
}

function computeAssessmentFromAnswers(answersObj, activeQuestions) {
  const answers = answersObj && typeof answersObj === "object" ? answersObj : {};
  const q = Array.isArray(activeQuestions) ? activeQuestions : [];
  const ids = q.map((x) => x?.id).filter((x) => x != null);

  const failed = [];
  const unsure = [];
  let yesCount = 0;
  let maxFindingGravity = 0;

  ids.forEach((id) => {
    const v = answers[id];
    const question = q.find((item) => String(item?.id) === String(id));
    const gravity = Number(question?.gravity || 0) || 0;

    if (v === true || v === "YES") yesCount += 1;
    else if (v === false || v === "NO") {
      failed.push(id);
      maxFindingGravity = Math.max(maxFindingGravity, gravity);
    } else if (v === "unsure" || v === "UNSURE") {
      unsure.push(id);
      maxFindingGravity = Math.max(maxFindingGravity, gravity);
    }
  });

  const total = ids.length || 0;
  const controlCoveragePct = total ? Math.round((yesCount / total) * 100) : 0;

  const governanceQuestions = q.filter((item) => {
    const category = String(item?.category || "").toLowerCase();
    return category === "governance" || category === "transparency";
  });
  const governanceIds = governanceQuestions.map((item) => item?.id).filter((id) => id != null);
  const governanceYesCount = governanceIds.reduce((count, id) => {
    const value = answers[id];
    return count + (value === true || value === "YES" ? 1 : 0);
  }, 0);
  const governanceBasePct = governanceIds.length
    ? Math.round((governanceYesCount / governanceIds.length) * 100)
    : 0;

  // A governance-maturity label must not become "Advanced" while a critical or
  // high-risk control gap is unresolved. These caps align with the existing
  // Governance Engine bands: <=60 Developing; <=80 Established; >=81 Advanced.
  const governanceCapPct = maxFindingGravity >= 10 ? 60 : maxFindingGravity >= 6 ? 80 : 100;
  const governanceMaturityPct = Math.min(governanceBasePct, governanceCapPct);

  return {
    control_coverage_pct: controlCoveragePct,
    // Backward-compatible alias. Customer-facing copy should describe this as
    // Control Coverage, not as a legal compliance certification.
    compliance_score_pct: controlCoveragePct,
    governance_maturity_score_pct: governanceMaturityPct,
    governance_maturity_base_pct: governanceBasePct,
    governance_maturity_cap_pct: governanceCapPct,
    governance_control_count: governanceIds.length,
    max_finding_gravity: maxFindingGravity,
    failed_question_ids: failed,
    unsure_question_ids: unsure,
    failed_count: failed.length,
    unsure_count: unsure.length,
    total_questions: total,
    active_question_ids: ids,
  };
}

function withTimeout(promise, timeoutMs, label = "timeout") {
  let t = null;
  const timeout = new Promise((_, reject) => {
    t = window.setTimeout(() => reject(new Error(label)), timeoutMs);
  });
  return Promise.race([promise, timeout]).finally(() => {
    if (t) window.clearTimeout(t);
  });
}

function getActiveBusinessId() {
  try {
    return localStorage.getItem(STORAGE_KEYS.ACTIVE_BUSINESS_ID) || "";
  } catch {
    return "";
  }
}

function loadBusinessPayload(businessId) {
  return safeParse(localStorage.getItem(`${STORAGE_KEYS.BUSINESS_PAYLOAD_PREFIX}${businessId}`), null);
}

function trySetFinalizeLock(snapshotId) {
  try {
    const k = `${FINALIZE_LOCK_PREFIX}${String(snapshotId || "")}`;
    const existing = localStorage.getItem(k);
    if (existing) return false; // already finalized/in-progress
    localStorage.setItem(k, new Date().toISOString());
    return true;
  } catch {
    // if storage fails, still allow finalize; but we also guard with in-memory ref
    return true;
  }
}

export default function AuditAssessment() {
  const navigate = useNavigate();
  const { loading: quotaEntitlementLoading, error: quotaEntitlementError, rawPlanKey: quotaPlanKey } = useEntitlement();
  const [quotaSaveError, setQuotaSaveError] = useState("");
  const [quotaServerConfirmed, setQuotaServerConfirmed] = useState(false);

  /**
   * ✅ Consolidated paywall gating:
   * Auth + paid checks are enforced globally by PaywallGuard (App.jsx).
   * This page should NOT redirect to /auth or /pricing.
   */

  const [businessId, setBusinessId] = useState("");
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [config, setConfig] = useState(null);

  const [isAnimating, setIsAnimating] = useState(false);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showExecutiveHelp, setShowExecutiveHelp] = useState(false);

  const [isFinalizing, setIsFinalizing] = useState(false);
  const [finalizeNote, setFinalizeNote] = useState("Saving results, generating exposure, and updating Reports…");

  // hard guard to prevent double-finalize within the same mount
  const finalizeInFlightRef = useRef(false);

  const keys = useMemo(() => {
    const bid = businessId || "single_default";
    return {
      answersKey: `${STORAGE_KEYS.ANSWERS_PREFIX}${bid}`,
      progressKey: `${STORAGE_KEYS.PROGRESS_PREFIX}${bid}`,
      canonicalBusinessKey: `${STORAGE_KEYS.RESULTS_CANONICAL_BUSINESS_PREFIX}${bid}`,
    };
  }, [businessId]);

  // Derived (memo) — used in callbacks
  const selectedJurisdictions = useMemo(() => normalizeJurisdictions(config), [config]);
  const activeQuestions = useMemo(() => getRelevantQuestions(config), [config]);
  const total = activeQuestions.length;

  const persistProgress = useCallback(
    (nextIndex, updatedAnswers) => {
      try {
        const completedCount = countAnswered(activeQuestions, updatedAnswers);
        localStorage.setItem(
          keys.progressKey,
          JSON.stringify({
            version: 1,
            business_id: businessId || "single_default",
            currentIndex: clampInt(nextIndex, 0, Math.max(0, total - 1)),
            totalQuestions: total,
            completedCount,
            updated_at_iso: new Date().toISOString(),
          })
        );
      } catch {
        // ignore
      }
    },
    [activeQuestions, businessId, keys.progressKey, total]
  );

  const clearProgress = useCallback(() => {
    try {
      localStorage.removeItem(keys.progressKey);
    } catch {
      // ignore
    }
  }, [keys.progressKey]);

  const clearAnswers = useCallback(() => {
    try {
      localStorage.removeItem(keys.answersKey);
    } catch {
      // ignore
    }
  }, [keys.answersKey]);

  useEffect(() => {
    const bid = getActiveBusinessId();
    const resolvedBid = bid || "single_default";
    setBusinessId(resolvedBid);

    const payload = bid ? loadBusinessPayload(bid) : null;
    const fallback = safeParse(localStorage.getItem(STORAGE_KEYS.CONFIG), null);
    const cfg = payload || fallback;

    if (!cfg) {
      navigate("/start-audit", { replace: true });
      return;
    }
    setConfig(cfg);

    const savedAnswers = safeParse(localStorage.getItem(`${STORAGE_KEYS.ANSWERS_PREFIX}${resolvedBid}`), {});
    if (savedAnswers && typeof savedAnswers === "object") setAnswers(savedAnswers);
    else setAnswers({});
  }, [navigate]);

  // ✅ ESLint-safe: clearProgress is stable and included
  useEffect(() => {
    if (!total) return;

    const answeredCount = countAnswered(activeQuestions, answers);
    const isComplete = total > 0 && answeredCount >= total;

    if (isComplete) {
      clearProgress();
      setCurrentIndex(0);
      return;
    }

    const savedProgress = safeParse(localStorage.getItem(keys.progressKey), null);
    const firstUnanswered = findFirstUnansweredIndex(activeQuestions, answers);

    const hinted =
      savedProgress && typeof savedProgress === "object"
        ? clampInt(savedProgress.currentIndex, 0, Math.max(0, total - 1))
        : null;

    const nextIdx = typeof hinted === "number" ? Math.max(firstUnanswered, hinted) : firstUnanswered;
    setCurrentIndex(nextIdx);
  }, [total, activeQuestions, answers, keys.progressKey, clearProgress]);

  const currentQuestion = useMemo(() => {
    if (!total) return null;
    return activeQuestions[currentIndex];
  }, [activeQuestions, currentIndex, total]);

  useEffect(() => {
    setShowExecutiveHelp(false);
  }, [currentIndex]);


  const progressPct = useMemo(() => {
    if (!total) return 0;
    return ((currentIndex + 1) / total) * 100;
  }, [currentIndex, total]);

  const currentAnswerValue = useMemo(() => {
    const id = currentQuestion?.id;
    if (!id) return null;
    return answers?.[id] ?? null;
  }, [answers, currentQuestion]);

  const effectiveSelectedAnswer = selectedAnswer != null ? selectedAnswer : currentAnswerValue;

  const gravityTooltip =
    "Gravity is an internal materiality signal (1–10). Higher gravity indicates this control is more likely to be treated as legally significant in enforcement practice. Gravity influences severity scoring, which can affect exposure modeling. It is not a fine amount or legal conclusion.";

  const finalizeAssessment = useCallback(
    async (updatedAnswers) => {
      // in-memory guard
      if (finalizeInFlightRef.current) return;
      finalizeInFlightRef.current = true;

      let watchdogFired = false;
      setQuotaSaveError("");
      setQuotaServerConfirmed(false);
      let serverConfirmed = false;

      // Paid quota must never navigate away on a timer before server confirmation.
      const watchdog = requiresPaidQuota(quotaPlanKey) ? null : window.setTimeout(() => {
        watchdogFired = true;
        try {
          window.location.assign("/reports");
        } catch {
          // ignore
        }
      }, FINALIZE_WATCHDOG_MS);

      try {
        setIsFinalizing(true);
        setFinalizeNote("Finalizing: building snapshot…");

        if (quotaEntitlementLoading || quotaEntitlementError || !quotaPlanKey) {
          throw new Error("Subscription verification is unavailable. Your answers remain saved.");
        }

        // create snapshot id ONCE
        const snapshot_id = requiresPaidQuota(quotaPlanKey)
          ? getOrCreatePendingAssessmentId(businessId) : `${Date.now()}`;

        // durable lock (prevents accidental second finalize on same snapshot)
        // If lock already exists, just bail to Reports safely.
        const lockOk = requiresPaidQuota(quotaPlanKey) ? true : trySetFinalizeLock(snapshot_id);
        if (!lockOk) {
          setFinalizeNote("Snapshot already finalized. Opening Reports…");
          navigate("/reports");
          return;
        }

        const configForEngine = {
          ...(config || null),
          business_id: businessId || "single_default",
          __active_question_ids: activeQuestions.map((q) => q.id),
          __active_questions_total: total,
          __jurisdictions_normalized: selectedJurisdictions,
        };

        const raw = calculateRobustRisk({
          annualRevenue: Number(configForEngine?.annualTurnover) || 0,
          peopleImpacted: Number(configForEngine?.userImpact) || 0,
          jurisdictions: selectedJurisdictions || [],
          answers: updatedAnswers,
          questions: activeQuestions,
        });
        const derivedAssessment = computeAssessmentFromAnswers(updatedAnswers, activeQuestions);

        const severity = computeSeverityFromAnswers(activeQuestions, updatedAnswers);

        const actionableQuestionIds = new Set([
          ...derivedAssessment.failed_question_ids.map((id) => String(id)),
          ...derivedAssessment.unsure_question_ids.map((id) => String(id)),
        ]);
        const questionSnapshot = activeQuestions
          .filter((question) => actionableQuestionIds.has(String(question?.id)))
          .map((question) => ({
            id: question?.id,
            title: question?.title || "",
            text: question?.text || "",
            gravity: Number(question?.gravity || 0),
            category: question?.category || "Assessment",
            jurisdictions: Array.isArray(question?.jurisdictions) ? [...question.jurisdictions] : [],
            guidance: question?.guidance || "",
            playbook_id: question?.playbook_id || null,
            answer_contract: question?.answer_contract || "YES_PASS_V1",
          }));

        const currency_code = configForEngine?.currency_code || "USD";
        const currency_symbol = configForEngine?.currency_symbol || "$";

        let canonical = {
          snapshot_id,
          version: "1.4",
          assessment_contract: "YES_PASS_V1",
          question_bank_version: getQuestionBankVersion(),
          created_at_iso: new Date().toISOString(),
          source: "detailed_assessment",
          business_id: businessId || "single_default",
          entity_name: configForEngine?.clientName || "Not specified",
          config: configForEngine || null,
          fx: null,

          assessment: {
            question_bank_version: getQuestionBankVersion(),
            question_snapshot: questionSnapshot,
            control_coverage_pct: derivedAssessment.control_coverage_pct,
            compliance_score_pct: derivedAssessment.compliance_score_pct,
            governance_maturity_score_pct: derivedAssessment.governance_maturity_score_pct,
            governance_maturity_base_pct: derivedAssessment.governance_maturity_base_pct,
            governance_maturity_cap_pct: derivedAssessment.governance_maturity_cap_pct,
            governance_control_count: derivedAssessment.governance_control_count,
            scoring_model_version: "weighted-gravity-10-6-4-2-v2",
            failed_question_ids: derivedAssessment.failed_question_ids,
            unsure_question_ids: derivedAssessment.unsure_question_ids,
            failed_count: derivedAssessment.failed_count,
            unsure_count: derivedAssessment.unsure_count,
            total_questions: derivedAssessment.total_questions,
            active_question_ids: derivedAssessment.active_question_ids,

            severity_tier: severity?.severity_tier,
            severity_tier_numeric: severity?.severity_tier_numeric,
            severity_score_0_100: severity?.severity_score_0_100,
            risk_score_pct: Number.isFinite(Number(severity?.risk_score)) ? Number(severity.risk_score) : null,
            weighted_risk_score: Number.isFinite(Number(severity?.weighted_risk_score)) ? Number(severity.weighted_risk_score) : null,
            risk_grade: severity?.risk_grade || null,
            risk_grade_base: severity?.risk_grade_base || null,
            risk_grade_floor: severity?.risk_grade_floor || null,
            risk_grade_floor_applied: Boolean(severity?.risk_grade_floor_applied),
            max_finding_gravity: severity?.max_finding_gravity || derivedAssessment.max_finding_gravity || 0,
            critical_finding_ids: Array.isArray(severity?.critical_finding_ids) ? severity.critical_finding_ids : [],
            high_risk_finding_ids: Array.isArray(severity?.high_risk_finding_ids) ? severity.high_risk_finding_ids : [],
            severity_explain: severity?.explain,
          },

          summary: {
            currency_code,
            currency_symbol,
            jurisdictions: selectedJurisdictions || [],
            annual_revenue_estimate: Number(configForEngine?.annualTurnover) || 0,
            people_impacted_per_month: Number(configForEngine?.userImpact) || 0,
            control_coverage_pct: derivedAssessment.control_coverage_pct,
            compliance_score_pct: derivedAssessment.compliance_score_pct,
            governance_maturity_score_pct: derivedAssessment.governance_maturity_score_pct,
            gross_exposure_ceiling_usd: Number(raw?.summary?.gross_exposure_ceiling_usd) || 0,
          },

          engine_output: raw && typeof raw === "object" ? raw : null,
          failedQuestions: derivedAssessment.failed_question_ids,
          unsureQuestions: derivedAssessment.unsure_question_ids,
          score: derivedAssessment.compliance_score_pct,

          inputs_echo: {
            severityTier: severity?.severity_tier_numeric ?? severity?.severity_tier,
            severityScore0to100: severity?.severity_score_0_100,
            riskGrade: severity?.risk_grade || null,
          },

          risk_exposure: null,
          risk_exposure_status: "PENDING",
          risk_exposure_error: null,
        };

        // Legacy tiers preserve early persistence; limited plans wait for
        // authoritative completion so denied audits cannot appear as reports.
        if (!requiresPaidQuota(quotaPlanKey)) {
          try { localStorage.setItem(STORAGE_KEYS.RESULTS_LEGACY, JSON.stringify(canonical)); } catch {}
          try { localStorage.setItem(STORAGE_KEYS.RESULTS_CANONICAL, JSON.stringify(canonical)); } catch {}
          try { localStorage.setItem(keys.canonicalBusinessKey, JSON.stringify(canonical)); } catch {}
        }

        // FX (optional)
        let fx = null;
        try {
          setFinalizeNote("Fetching FX rates (planning disclosure)…");
          fx = await withTimeout(
            getFxRatesPlanning({ maxAgeHours: 12, forceRefresh: false }),
            FX_TIMEOUT_MS,
            "FX_TIMEOUT"
          );
        } catch {
          fx = null;
        }
        canonical.fx = fx || null;

        // Exposure compute
        try {
          setFinalizeNote("Computing multi-regime exposure…");
          canonical.risk_exposure = computePaidReportExposure({
            configForEngine,
            canonicalPayload: canonical,
          });
          canonical.risk_exposure_status = "OK";

          if (canonical.risk_exposure && canonical.risk_exposure.__error) {
            canonical.risk_exposure_status = "ERROR";
            canonical.risk_exposure_error = String(canonical.risk_exposure.__error);
          }
        } catch (err) {
          canonical.risk_exposure = null;
          canonical.risk_exposure_status = "ERROR";
          canonical.risk_exposure_error = String(err?.message || err || "Exposure error");
        }

        // Finalization happens only AFTER exposure calculation succeeds.
        if (requiresPaidQuota(quotaPlanKey)) {
          if (quotaEntitlementLoading || quotaEntitlementError) {
            throw new Error("Subscription verification is temporarily unavailable. Your answers are retained.");
          }
          if (countAnswered(activeQuestions, updatedAnswers) !== total ||
              canonical.risk_exposure_status !== "OK") {
            throw new Error("Assessment cannot be finalized until answers and exposure calculation are complete.");
          }
          setFinalizeNote("Confirming subscription allowance and securely saving snapshot…");
          const confirmation = await finalizePaidAssessment({
            planKey: quotaPlanKey, businessId: businessId || "single_default",
            assessmentId: snapshot_id, snapshot: canonical, answers: updatedAnswers,
          });
          canonical = confirmation.snapshot; // Server-preserved immutable replay payload.
          serverConfirmed = true;
        }

        // Persist final canonical (with exposure)
        try {
          localStorage.setItem(STORAGE_KEYS.RESULTS_LEGACY, JSON.stringify(canonical));
        } catch {}
        try {
          localStorage.setItem(STORAGE_KEYS.RESULTS_CANONICAL, JSON.stringify(canonical));
        } catch {}
        try {
          localStorage.setItem(keys.canonicalBusinessKey, JSON.stringify(canonical));
        } catch {}

        // ✅ CRITICAL: Append snapshot history EXACTLY ONCE (after exposure is ready)
        if (requiresPaidQuota(quotaPlanKey)) {
          // Server confirmation has already happened. If this local cache fails,
          // keep the retry identity and the draft; NEVER claim the audit failed.
          appendAuditSnapshot(canonical);
          // This must succeed before a distinct new assessment can be started.
          // If it fails, the user sees 'securely saved', with a recovery retry.
          clearPendingAssessmentId(businessId, snapshot_id);
        } else {
          try { appendAuditSnapshot(canonical); } catch {}
        }

        clearProgress();
        clearAnswers();
        try {
          window.dispatchEvent(new Event("storage"));
          window.dispatchEvent(new CustomEvent("aifine:audit-completed", { detail: { businessId } }));
        } catch {}
        setFinalizeNote("Opening Reports…");

        if (!watchdogFired) {
          navigate("/reports");
          window.setTimeout(() => {
            try {
              if (window.location.pathname !== "/reports") window.location.assign("/reports");
            } catch {}
          }, 450);
        }
      } catch (error) {
        if (requiresPaidQuota(quotaPlanKey) || !quotaPlanKey ||
            quotaEntitlementLoading || quotaEntitlementError) {
          // Differentiate failure BEFORE confirmation from a local cache error AFTER it.
          setQuotaServerConfirmed(serverConfirmed);
          setQuotaSaveError(serverConfirmed
            ? "Your Assessment WAS completed and securely saved. The secure server has your report, but this browser could not cache it. Your answers are retained. Retry recovery or open Reports from another browser."
            : (error?.message || "Completion has not been confirmed. Your answers remain saved; retry securely."));
        } else {
          throw error;
        }
      } finally {
        window.clearTimeout(watchdog);
        setIsFinalizing(false);
        finalizeInFlightRef.current = false;
      }
    },
    [
      activeQuestions,
      businessId,
      clearProgress,
      clearAnswers,
      config,
      keys.canonicalBusinessKey,
      navigate,
      selectedJurisdictions,
      total,
      quotaPlanKey,
      quotaEntitlementLoading,
      quotaEntitlementError,
    ]
  );

  const beginAnswerFlow = useCallback(
    (answer) => {
      if (!config || !currentQuestion) return;
      if (isAnimating || isFinalizing) return;

      setSelectedAnswer(answer);
      setIsAnimating(true);

      window.setTimeout(() => {
        const updatedAnswers = { ...answers, [currentQuestion.id]: answer };
        setAnswers(updatedAnswers);
        try {
          localStorage.setItem(keys.answersKey, JSON.stringify(updatedAnswers));
        } catch {}

        setSelectedAnswer(null);
        setIsAnimating(false);

        if (currentIndex < total - 1) {
          const nextIndex = currentIndex + 1;
          setCurrentIndex(nextIndex);
          persistProgress(nextIndex, updatedAnswers);
          return;
        }

        finalizeAssessment(updatedAnswers);
      }, PULSE_MS);
    },
    [
      answers,
      config,
      currentIndex,
      currentQuestion,
      finalizeAssessment,
      isAnimating,
      isFinalizing,
      keys.answersKey,
      persistProgress,
      total,
    ]
  );

  const canGoNext = useMemo(() => {
    if (!currentQuestion?.id) return false;
    return isAnsweredValue(answers?.[currentQuestion.id]);
  }, [answers, currentQuestion]);

  const goPrev = useCallback(() => {
    if (isAnimating || isFinalizing) return;
    const nextIndex = Math.max(0, currentIndex - 1);
    setCurrentIndex(nextIndex);
    persistProgress(nextIndex, answers);
  }, [answers, currentIndex, isAnimating, isFinalizing, persistProgress]);

  const goNext = useCallback(() => {
    if (isAnimating || isFinalizing) return;
    if (!canGoNext) return;
    const nextIndex = Math.min(total - 1, currentIndex + 1);
    setCurrentIndex(nextIndex);
    persistProgress(nextIndex, answers);
  }, [answers, canGoNext, currentIndex, isAnimating, isFinalizing, persistProgress, total]);

  const exitToDashboard = useCallback(() => {
    if (isFinalizing) return;
    persistProgress(currentIndex, answers);
    navigate("/dashboard");
  }, [answers, currentIndex, isFinalizing, navigate, persistProgress]);

  if (!config || !currentQuestion) return null;

  const cardBase = "w-full rounded-2xl border p-6 text-left transition-all duration-200 select-none";
  const disabledDim = isAnimating || isFinalizing ? "opacity-50 pointer-events-none" : "";
  const selectedRing = "ring-2 ring-offset-2";
  const pulseClass = "animate-pulse";

  const yesIsSelected = effectiveSelectedAnswer === true || effectiveSelectedAnswer === "YES";
  const noIsSelected = effectiveSelectedAnswer === false || effectiveSelectedAnswer === "NO";
  const unsureIsSelected = effectiveSelectedAnswer === "unsure" || effectiveSelectedAnswer === "UNSURE";
  const isFinalQuestion = total > 0 && currentIndex === total - 1;

  return (
    <div className="flex h-screen bg-slate-50 font-inter text-slate-900 overflow-hidden">
      <Sidebar />

      <main className="flex-1 flex flex-col p-8 md:p-12 overflow-auto relative">
        {quotaSaveError ? (
          <div role="alert" className="mb-4 rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-950">
            <div className="font-bold">{quotaServerConfirmed ? "Assessment securely saved" : "Assessment completion not confirmed"}</div>
            <p className="mt-1">{quotaSaveError}</p>
            <button type="button" className="mt-3 rounded-lg bg-slate-900 px-4 py-2 text-white"
              onClick={() => finalizeAssessment(answers)}>{quotaServerConfirmed ? "Retry report recovery" : "Retry secure completion"}</button>
          </div>
        ) : null}
        {isFinalizing ? (
          <div className="absolute inset-0 z-50 flex items-center justify-center bg-white/70 backdrop-blur-sm">
            <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-5 shadow-sm text-center">
              <div className="text-sm font-black text-slate-900">Finalizing snapshot…</div>
              <div className="mt-1 text-xs text-slate-600">{finalizeNote}</div>
              <div className="mt-4 h-2 w-full rounded-full bg-slate-200 overflow-hidden">
                <div className="h-full w-1/2 bg-blue-600 animate-pulse" />
              </div>
              <div className="mt-3 text-[11px] text-slate-500">
                {requiresPaidQuota(quotaPlanKey)
                  ? "Your answers remain saved until the secure completion is confirmed."
                  : "If this takes more than a few seconds, the system will automatically open Reports."}
              </div>
            </div>
          </div>
        ) : null}

        <div className="mx-auto w-full max-w-5xl">
          <div className="flex items-end justify-between">
            <div>
              <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">
                Question {currentIndex + 1} of {total}
              </div>
              <div className="mt-1 text-2xl md:text-3xl font-bold tracking-tight">
                {currentQuestion.category || "Assessment"}
              </div>

              {selectedJurisdictions.length ? (
                <div className="mt-2 text-xs text-slate-500">
                  Scope: <span className="font-semibold">{selectedJurisdictions.join(", ")}</span>
                </div>
              ) : null}
            </div>

            <div className="text-right">
              <div className="text-3xl md:text-4xl font-extrabold text-slate-300">{Math.round(progressPct)}%</div>

              <div className="group relative mt-1 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1">
                <ShieldAlert size={14} className="text-slate-700" />
                <span className="text-[11px] font-extrabold text-slate-900">Gravity: {currentQuestion.gravity}/10</span>
                <button type="button" className="inline-flex" aria-label="What is gravity?">
                  <Info size={14} className="text-slate-500" />
                </button>
                <div role="tooltip" className="pointer-events-none absolute right-0 top-8 z-30 hidden w-80 rounded-xl border border-slate-200 bg-white p-3 text-left text-xs font-medium leading-5 text-slate-600 shadow-xl group-hover:block group-focus-within:block">
                  {gravityTooltip}
                </div>
              </div>
            </div>
          </div>

          <div className="mt-4 h-2 w-full rounded-full bg-slate-200 overflow-hidden">
            <div className="h-full bg-blue-600 transition-all duration-500 ease-out" style={{ width: `${progressPct}%` }} />
          </div>

          <div className="mt-4 flex justify-end">
            <button
              type="button"
              onClick={exitToDashboard}
              disabled={isFinalizing}
              className="text-xs font-semibold text-slate-500 hover:text-slate-900 disabled:opacity-40"
            >
              Exit to Dashboard
            </button>
          </div>
        </div>

        <div className="mx-auto mt-8 w-full max-w-5xl rounded-3xl border border-slate-200 bg-white p-8 md:p-10 shadow-sm">
          {isFinalQuestion ? (
            <div className="mb-6 rounded-2xl border border-blue-200 bg-blue-50 px-5 py-3 md:flex md:items-center md:gap-3">
              <div className="shrink-0 font-black text-blue-700">Final Question</div>
              <div className="mt-1 text-sm leading-6 text-slate-700 md:mt-0">Once you select your answer, your assessment will be completed and you will be taken directly to the Reports page.</div>
            </div>
          ) : null}

          <div className="inline-flex items-center rounded-full bg-slate-100 px-3 py-1 text-[11px] font-semibold tracking-widest text-slate-600">
            {currentQuestion.category || "GENERAL"}
          </div>

          <h2 className="mt-4 text-xl md:text-2xl font-bold tracking-tight text-slate-900">{currentQuestion.text}</h2>

          {currentIndex === 0 ? (
            <div className="mt-4 rounded-xl border border-blue-100 bg-blue-50 p-4 text-sm text-slate-700">
              <div className="font-semibold text-slate-900">You are not expected to know every answer personally.</div>
              <div className="mt-1">
                Some questions may require input from your technology, legal, privacy, HR, security, or compliance teams.
                Choose <span className="font-semibold">UNSURE</span> when you cannot verify that a requirement is satisfied.
                AFP will identify those items for follow-up.
              </div>
            </div>
          ) : null}

          {currentQuestion.executive_meaning ? (
            <div className="mt-4">
              <button
                type="button"
                onClick={() => setShowExecutiveHelp((open) => !open)}
                aria-expanded={showExecutiveHelp}
                className="inline-flex items-center gap-2 text-sm font-semibold text-blue-700 hover:text-blue-900"
              >
                <span>{showExecutiveHelp ? "−" : "+"}</span>
                <span>Not sure what this means?</span>
              </button>

              {showExecutiveHelp ? (
                <div className="mt-3 rounded-xl border border-blue-100 bg-blue-50 p-4 text-sm text-slate-700">
                  <div>
                    <div className="font-semibold text-slate-900">What this means</div>
                    <div className="mt-1">{currentQuestion.executive_meaning}</div>
                  </div>

                  <div className="mt-4">
                    <div className="font-semibold text-slate-900">Who may know</div>
                    <div className="mt-1">{currentQuestion.executive_who_knows}</div>
                  </div>

                  <div className="mt-4 border-t border-blue-100 pt-4">
                    <div>
                      <span className="font-semibold text-slate-900">Answer YES when: </span>
                      {currentQuestion.executive_yes_when}
                    </div>
                    <div className="mt-2">
                      <span className="font-semibold text-slate-900">Choose UNSURE when: </span>
                      {currentQuestion.executive_unsure_when}
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}

          <div className="mt-8">
            <div className="text-sm font-semibold text-slate-700 mb-3">Select your response:</div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <button
                type="button"
                onClick={() => beginAnswerFlow(true)}
                className={[
                  cardBase,
                  disabledDim,
                  "border-slate-200 bg-white hover:border-slate-300",
                  yesIsSelected ? `${selectedRing} ring-green-500 ${pulseClass}` : "",
                  yesIsSelected ? "bg-green-50 border-green-200" : "",
                ].join(" ")}
              >
                <div className="flex items-start gap-3">
                  <div
                    className={[
                      "mt-1 h-4 w-4 rounded-full border",
                      yesIsSelected ? "border-green-600 bg-green-600" : "border-slate-300 bg-white",
                    ].join(" ")}
                  />
                  <div>
                    <div className="font-semibold text-slate-900">Yes</div>
                    <div className="text-sm text-slate-500">Requirement satisfied / appropriate controls in place</div>
                  </div>
                </div>
              </button>

              <button
                type="button"
                onClick={() => beginAnswerFlow(false)}
                className={[
                  cardBase,
                  disabledDim,
                  "border-slate-200 bg-white hover:border-slate-300",
                  noIsSelected ? `${selectedRing} ring-red-500 ${pulseClass}` : "",
                  noIsSelected ? "bg-red-50 border-red-200" : "",
                ].join(" ")}
              >
                <div className="flex items-start gap-3">
                  <div
                    className={[
                      "mt-1 h-4 w-4 rounded-full border",
                      noIsSelected ? "border-red-600 bg-red-600" : "border-slate-300 bg-white",
                    ].join(" ")}
                  />
                  <div>
                    <div className="font-semibold text-slate-900">No</div>
                    <div className="text-sm text-slate-500">Requirement not satisfied / control gap exists</div>
                  </div>
                </div>
              </button>

              <button
                type="button"
                onClick={() => beginAnswerFlow("unsure")}
                className={[
                  cardBase,
                  disabledDim,
                  "border-slate-200 bg-white hover:border-slate-300",
                  unsureIsSelected ? `${selectedRing} ring-amber-500 ${pulseClass}` : "",
                  unsureIsSelected ? "bg-amber-50 border-amber-200" : "",
                ].join(" ")}
              >
                <div className="flex items-start gap-3">
                  <div
                    className={[
                      "mt-1 h-4 w-4 rounded-full border",
                      unsureIsSelected ? "border-amber-600 bg-amber-600" : "border-slate-300 bg-white",
                    ].join(" ")}
                  />
                  <div>
                    <div className="font-semibold text-slate-900">Unsure</div>
                    <div className="text-sm text-slate-500">Requires further investigation / verification</div>
                  </div>
                </div>
              </button>
            </div>

            <div className="mt-8 flex items-center justify-between border-t pt-6">
              <button
                type="button"
                disabled={currentIndex === 0 || isAnimating || isFinalizing}
                onClick={goPrev}
                className="text-sm text-slate-500 hover:text-slate-900 disabled:opacity-40"
              >
                ← Previous Question
              </button>

              <button
                type="button"
                disabled={!canGoNext || isAnimating || isFinalizing}
                onClick={goNext}
                className={[
                  "rounded-xl px-4 py-2 text-sm font-semibold",
                  canGoNext && !isAnimating && !isFinalizing
                    ? "bg-slate-900 text-white hover:bg-slate-800"
                    : "bg-slate-100 text-slate-700 opacity-70 cursor-not-allowed",
                ].join(" ")}
                title={canGoNext ? (isFinalQuestion ? "Complete assessment and open Reports" : "Go to next question") : "Select an answer to continue"}
              >
                {isFinalQuestion ? "Complete Assessment →" : "Next Question →"}
              </button>
            </div>

            <div className="mt-4 text-xs text-slate-500">
              Your progress is saved automatically. You can exit and resume later.
            </div>
          </div>
        </div>

        <div className="mx-auto w-full max-w-5xl mt-10 pb-8 text-xs text-slate-400">
          © 2026 Advanced Advisory Services, LLC. All Rights Reserved.
        </div>
      </main>
    </div>
  );
}